import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Steps } from 'rsuite';
import { ACTIVITY_TYPES, ADDED_NOTE } from '@/constants';
import { username } from '@/user/user-selectors';
import styled from 'styled-components';
import { useComplaintsStatus } from '../../utils/useComplaintsStatus';
import { ActivityDocumentLink } from './activity-document-link';
import { ComplaintDetailsLog } from './complaint-details-log';
import { EventLog } from './event-log';
import { NoteLog } from './note-log';

const StyledStepItem = styled(Steps.Item)`
  .rs-steps-item-title {
    width: 100%;
  }
`;

const Circle = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: black;
`;

const NoDescriptionComponent = () => null;

/**
 * Map an activity type to the component that should render a detailed description
 * of the activity underneath the activity's one-line summary in the activity feed
 */
const activityTypeToDescriptionComponent = {
  [ACTIVITY_TYPES.NEW_COMPLAINT]: ComplaintDetailsLog,
  [ACTIVITY_TYPES.COMPLAINT_UPDATE]: NoDescriptionComponent,
  [ACTIVITY_TYPES.GENERATED_FIRST_OUTREACH_LETTER]: NoDescriptionComponent,
  [ACTIVITY_TYPES.GENERATED_SECOND_OUTREACH_LETTER]: NoDescriptionComponent,
  [ACTIVITY_TYPES.GENERATED_THIRD_OUTREACH_LETTER]: NoDescriptionComponent,
  [ACTIVITY_TYPES.NOTE]: NoteLog,
};

/**
 * Returns a customized "source" and "event" for an activity
 * that represents a newly-raised complaint.
 *
 * The source is set to 'Complaint' to get the right formatting
 * of the event message on the UI (e.g. "Complaint 123 raised").
 * This function make an assumption about how `<EventLog ... />` treats
 * the source and event props.
 *
 * Another way to look at this is that the source is actually
 * the Complaints App - putting down the complainant's name may
 * not make sense as they do not correspond to a Rentalscape/Cognito
 * user anyway
 * @param activityValue The `value` field from the activity object
 * @returns {{source: string, event: string}}
 */
function extractSourceAndEventForNewComplaint(activityValue) {
  const { complaintDetails } = activityValue;

  return {
    event: `${complaintDetails?.complaintId} raised`,
    source: 'Complaint',
  };
}

/**
 * Complaint status update activities coming from the stroracle-api are
 * expected to have a field `complaintDetails` in their `value` which
 * contains these fields
 * @typedef {Object} ActivityComplaintUpdateDetails
 * @property {number} complaintId Numeric ID of the updated complaint
 * @property {string} newStatus New status of the complaint
 */

/**
 * Returns a customized "source" and "event" for an activity
 * that represents a note left on property.
 *
 * The source is set to the author of the note, as note activities
 * do not seem to have a populated `source` field.
 *
 * @param activityValue The `value` field from the activity object
 * @returns {{source: *, event: string}}
 */
function extractSourceAndEventForNote(activityValue) {
  const { author } = activityValue;
  return {
    event: ADDED_NOTE,
    source: author?.name,
  };
}

function extractSourceAndEventForOutreachLetter(activity) {
  const { source, event, message } = activity;
  return {
    event: `${event} ${message}`,
    eventReactNode: (
      <>
        {event}{' '}
        <ActivityDocumentLink activity={activity}>
          {message}
        </ActivityDocumentLink>
      </>
    ),
    source,
  };
}

/**
 * This component will render an item in the activity feed.
 *
 * @param {string} props.type type of activity feed item (note, event)
 * @param {*} props.value
 * @param {number} props.index position of item in activityFeed array. Useful for edits
 */
export function FeedItem({ type, value, index }) {
  const currentUserEmail = useSelector(username);

  const { complaintStatusLabelFactory } = useComplaintsStatus();

  /**
   * Returns a customized "source" and "event" for an activity
   * that represents an update to a complaint status.
   *
   * @param activityValue The `value` field from the activity object
   * @param {ActivityComplaintUpdateDetails} activityValue.complaintDetails ID and updated status of the complaint
   * @returns {{event: string}}
   */
  const extractSourceAndEventForComplaintUpdate = useCallback(
    (activityValue) => {
      const { complaintDetails } = activityValue;

      const complaintId = complaintDetails?.complaintId;

      const newStatus = complaintStatusLabelFactory(
        complaintDetails?.newStatus
      );
      return {
        event: `has set status of complaint ${complaintId} to '${newStatus}'`,
      };
    },
    [complaintStatusLabelFactory]
  );

  /**
   * Maps an activity type to a function that returns overridden values
   * for `source` and/or `event` of the activity
   */
  const activityTypeToEventAndSourceExtractor = useMemo(
    () => ({
      [ACTIVITY_TYPES.NEW_COMPLAINT]: extractSourceAndEventForNewComplaint,
      [ACTIVITY_TYPES.COMPLAINT_UPDATE]:
        extractSourceAndEventForComplaintUpdate,
      [ACTIVITY_TYPES.NOTE]: extractSourceAndEventForNote,
      [ACTIVITY_TYPES.GENERATED_FIRST_OUTREACH_LETTER]:
        extractSourceAndEventForOutreachLetter,
      [ACTIVITY_TYPES.GENERATED_SECOND_OUTREACH_LETTER]:
        extractSourceAndEventForOutreachLetter,
      [ACTIVITY_TYPES.GENERATED_THIRD_OUTREACH_LETTER]:
        extractSourceAndEventForOutreachLetter,
    }),
    [extractSourceAndEventForComplaintUpdate]
  );

  const getOverriddenSourceAndEvent =
    activityTypeToEventAndSourceExtractor[type] || (() => ({}));

  // Get `source` and `event` from the activity, unless overridden by `getOverriddenSourceAndEvent`.
  // `getOverriddenSourceAndEvent` can return an object with an updated `source` and/or `event`
  const { source, event, eventReactNode } = {
    ...value,
    ...getOverriddenSourceAndEvent(value),
  };

  // Use the NoteLog as a default description component for other activites
  const DescriptionComponent =
    activityTypeToDescriptionComponent[type] || NoteLog;

  return (
    <StyledStepItem
      title={
        <EventLog
          event={event}
          eventReactNode={eventReactNode}
          dateTime={value.dateTime}
          source={source}
        />
      }
      icon={<Circle />}
      description={
        <DescriptionComponent
          {...value}
          index={index}
          currentUserEmail={currentUserEmail}
        />
      }
    />
  );
}
